import {isString, toNumberOrUndefined} from './type'
import {easingPattern} from './easingPattern'

const DEFAULT_EASING = 'easeOutQuad';
const SHORT_DISTANCE_THRESHOLD = 1333;
const SHORT_DURATION_MS = 750;
const LONG_DURATION_MS = 1000;
const MAX_ANIMATION_TIME_MS = 10000;

/**
 * 高精度タイマーが使える場合はそれを優先して現在時刻(ms)を返す。
 * @returns {number}
 */
function getTime() {
    return window.performance.now();
}

/**
 * 値を 0〜1 に丸める。
 * @param {number} value
 * @returns {number}
 */
function clamp01(value) {
    if (value < 0) return 0;
    if (value > 1) return 1;
    return value;
}

/**
 * 距離に応じてアニメーション時間(ms)を決める。
 * @param {number} distance
 * @returns {number}
 */
function resolveDuration(distance) {
    return Math.abs(distance) > SHORT_DISTANCE_THRESHOLD ? LONG_DURATION_MS : SHORT_DURATION_MS;
}

/**
 * 既存仕様に合わせて adjust_pos を正規化する。
 * - falsy のときは window.scroll_adjust_pos を使う
 * - 数値でない場合は 0
 * @param {number | undefined} adjustPos
 * @returns {number}
 */
function normalizeAdjustPos(adjustPos) {
    const fallback = window.scroll_adjust_pos ? window.scroll_adjust_pos : 0;
    const resolved = adjustPos ? adjustPos : fallback;

    if (resolved && !isFinite(resolved)) {
        return 0;
    }

    return Number(resolved) || 0;
}

/**
 * スクロール対象を文字列セレクタまたは要素から解決する。
 * @param {string | Element | null | undefined} tgt
 * @returns {Element | null}
 */
function resolveTargetElement(tgt) {
    if (!tgt) {
        return null;
    }

    if (isString(tgt)) {
        // #id 指定は querySelector より getElementById の方が意図が明確で高速
        if (tgt.match(/^#\S+$/)) {
            return document.getElementById(tgt.replace(/^#/, ''));
        }

        return document.querySelector(tgt);
    }

    if (tgt.nodeType === 1) {
        return tgt;
    }

    return null;
}

/**
 * スクロール対象ノードを返す。
 * @returns {Element}
 */
function resolveScrollTarget() {
    return document.scrollingElement || document.documentElement;
}

/**
 * ID指定したDOMまでスクロールする
 * @param {string | Element} tgt - ID文字列（例: #target）または要素
 * @param {string} [easing] - easingPattern に渡すイージング名
 * @param {number} [adjust_pos] - 到達位置に加算するオフセット(px)
 * @returns {false | void}
 */
export function smoothScroll(tgt, easing, adjust_pos) {
    if (!tgt) {
        return false;
    }

    const targetElement = resolveTargetElement(tgt);
    if (!targetElement) {
        return;
    }

    const adjustPos = normalizeAdjustPos(adjust_pos);
    const scrollTarget = resolveScrollTarget();
    const startPos = scrollTarget.scrollTop;
    const endPos = Math.max(targetElement.getBoundingClientRect().top + startPos + adjustPos, 0);
    const distance = endPos - startPos;
    const duration = resolveDuration(distance);
    const easingName = easing || DEFAULT_EASING;

    const startTime = getTime();

    (function loop() {
        const elapsedTime = Math.floor(getTime() - startTime);
        const progress = clamp01(elapsedTime / duration);

        scrollTarget.scrollTop =  startPos + (distance * easingPattern(easingName, progress));

        // 完了済み or 異常に長いアニメーションは打ち切る
        if (progress !== 1 && elapsedTime <= MAX_ANIMATION_TIME_MS) {
            window.requestAnimationFrame(loop);
        }
    })();

    return false;
}

/**
 * window.location.hashでID指定したDOMまでスクロールする
 * @param {string} hash - ex. _elementId
 * @returns {void}
 * */
export function smoothScrollByHash(hash) {
    if (!hash || !/^_/.test(hash)) return;

    // ヘッダーがposition:stickyなので高さを考慮
    const headerElements = document.getElementsByClassName('l-service_header');
    const headerOffset = headerElements.length ? toNumberOrUndefined(headerElements[0].offsetHeight * -1) : '';

    // 仕様上 hash は "_id" 形式なので、先頭 _ を除去して id セレクタに変換
    smoothScroll('#' + hash.replace(/^_/, ''), '', headerOffset);

    if (window.history && window.history.replaceState) {
        setTimeout(function () {
            const path = window.location.pathname + window.location.search;
            const stateObj = {path: path, hash: hash, title: 'afterSmoothScroll'};
            history.replaceState(stateObj, null, path);
        }, 2000);
    }
}
